// useRazorpayPayment.js
import { useCallback } from "react";
import { getDatabase, ref, set } from "firebase/database";
import { auth } from "../services/firebase";

/**
 * ✅ Handles Razorpay checkout + Firebase logging
 * - Creates Billing record (all attempts)
 * - Creates Purchase record (only success)
 * - Updates Access record (for unlocking content)
 * - Saves validity + expiry
 * - Triggers custom success/failure modals via callbacks
 */
export default function useRazorpayPayment() {
    const db = getDatabase();

    // 💳 Start Razorpay flow
    const initiatePayment = useCallback(
        async ({ price, pack, name, phone, coupon, onSuccess, onFailure }) => {
            const user = auth.currentUser;
            if (!user) {
                alert("Please log in to continue payment.");
                return;
            }

            if (!pack || !pack.packId || !pack.domainId || !pack.price) {
                alert("Invalid pack details.");
                return;
            }

            const uid = user.uid;
            const domainId = pack.domainId;
            const packId = pack.packId;
            const amountToPay = price * 100; // Razorpay expects paise

            // 🧾 Record “payment intent”
            const billingRef = ref(db, `Payments/Billings/${uid}/${domainId}/${packId}`);
            const initialData = {
                packId,
                packName: pack.name,
                domainId,
                name,
                phone,
                couponCode: coupon || "N/A",
                mrp: pack.mrp || pack.price,
                finalPrice: price,
                validity: pack.validity || null,
                status: "initiated",
                createdAt: Date.now(),
            };
            await set(billingRef, initialData);

            // 🪙 Razorpay checkout setup
            const options = {
                key: process.env.REACT_APP_RAZORPAY_TEST_KEY_ID, // ✅ from .env file
                amount: amountToPay,
                currency: "INR",
                name: "Crack Interview",
                description: `Payment for ${pack.name}`,
                image: "/logo.png",

                // ✅ Success handler
                handler: async function (response) {
                    const successData = {
                        ...initialData,
                        status: "success",
                        razorpayPaymentId: response.razorpay_payment_id,
                        razorpayOrderId: response.razorpay_order_id || null,
                        razorpaySignature: response.razorpay_signature || null,
                        updatedAt: Date.now(),
                    };

                    // Save success details to Billing
                    await set(billingRef, successData);

                    // ✅ Compute expiry timestamp
                    const expiresAt = pack.validity
                        ? Date.now() + parseInt(pack.validity) * 24 * 60 * 60 * 1000 // days → ms
                        : null;

                    // ✅ Save under Purchases (confirmed success)
                    const purchaseRef = ref(db, `Payments/Purchases/${uid}/${domainId}/${packId}`);
                    const finalSuccessData = {
                        ...successData,
                        validity: pack.validity || null,
                        expiresAt,
                    };
                    await set(purchaseRef, finalSuccessData);

                    // ✅ Grant access (with expiry info)
                    const accessRef = ref(db, `Payments/Access/${uid}/${domainId}/${packId}`);
                    await set(accessRef, {
                        activatedAt: Date.now(),
                        validity: pack.validity || null,
                        expiresAt,
                    });

                    if (onSuccess) onSuccess(response);
                },

                // Prefill Razorpay checkout form
                prefill: { name, contact: phone },

                // Metadata
                notes: {
                    packId,
                    packName: pack.name,
                    uid,
                    couponCode: coupon || "N/A",
                },

                theme: { color: "#2563eb" },

                // ❌ On close / dismiss
                modal: {
                    ondismiss: async function () {
                        const failData = {
                            ...initialData,
                            status: "failed_or_cancelled",
                            updatedAt: Date.now(),
                        };
                        await set(billingRef, failData);
                        if (onFailure) onFailure();
                    },
                },
            };

            // 🚀 Open Razorpay Checkout
            if (window.Razorpay) {
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                alert("Razorpay SDK not loaded. Please refresh the page.");
            }
        },
        [db]
    );

    return { initiatePayment };
}
